import _ from "lodash";
import ko from "knockout";
import Promise from "bluebird";
import { v5 as uuidv5 } from "uuid";
import apiCore from "services/apiCore";
import authService from "services/authService";
import Conversation from "models/Conversation";
import Recording from "models/Recording";
import Annotation from "models/Annotation";
import PendingEvaluations from "models/PendingEvaluations";
import ObservableModel from "util/ObservableModel";
import localeService from "services/localeService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

// ---------------------------------------------------------------------------
// Create-evaluation deduplication (idempotency)
// ---------------------------------------------------------------------------
//
// When the QM_19313_EVALUATION_DEDUPLICATION feature toggle is on, createEvaluation
// sends an Idempotency-Key header so the backend can deduplicate requests.
//
// INTENDED BEHAVIOR:
//
// 1. Deduplicate retries and double-submits
//    - The same key is sent for repeated requests with the same form data until
//      the request succeeds. If the user double-clicks or the client retries after
//      a timeout, the backend treats it as the same logical request (returns 201
//      with the existing evaluation or 409 if already created).
//
// 2. Allow a new evaluation when form data changes
//    - The key includes: conversationId, evaluationFormId, agentId, queueId,
//      evaluatorId. If the user changes any of these (e.g. different form or
//      agent), the key changes and the backend creates a new evaluation.
//
// 3. Allow another evaluation with the same form data after success
//    - A rotation component is incremented only on successful create. So after
//      a successful create, the next request with the same form data gets a
//      different key and the backend creates a new evaluation (user can create
//      multiple evaluations for the same conversation/form/agent/etc.).
//
// 4. Backend contract
//    - Backend returns 201 on first create for a key, and 409 Conflict for a
//      duplicate key. The UI treats 409 (or body code quality.evaluation.already.in.progress)
//      as "duplicate evaluation detected" and shows the corresponding message.
//
// ---------------------------------------------------------------------------

// Namespace for create-evaluation idempotency keys.
const CREATE_EVALUATION_IDEMPOTENCY_NAMESPACE =
    "a8f5e2d1-4b3c-4e9a-8f2d-7c1b6e9a0d4f";

// Incremented on successful create so the next request gets a different key (see behavior 3 above).
let createEvaluationIdempotencyRotation = 0;

/**
 * Resets the idempotency rotation counter. For test isolation only.
 * @internal
 */
export function __resetCreateEvaluationIdempotencyForTests() {
    createEvaluationIdempotencyRotation = 0;
}

/**
 * Generates a deterministic idempotency key from component strings (UUID v5).
 *
 * @param {string[]} components - Array of strings to include in the key
 * @returns {string} Deterministic idempotency key (UUID v5)
 */
function generateIdempotencyKey(components) {
    const name = components.join("|");
    return uuidv5(name, CREATE_EVALUATION_IDEMPOTENCY_NAMESPACE);
}

/**
 * Generates an idempotency key for createEvaluation requests (UUID v5).
 * See the "Create-evaluation deduplication" block above for full behavior.
 * Key = uuidv5(conversationId | evaluationFormId | agentId | queueId | evaluatorId | rotation).
 *
 * @param {Object} evaluation - The evaluation object to create
 * @returns {string} Idempotency key (UUID v5)
 */
function generateIdempotencyKeyForCreateEvaluation(evaluation) {
    const components = [
        evaluation.conversation?.id || "",
        evaluation.evaluationForm?.id || "",
        evaluation.agent?.id || "no-agent",
        evaluation.queue?.id || "no-queue",
        evaluation.evaluator?.id || "auto-submit",
        String(createEvaluationIdempotencyRotation),
    ];

    return generateIdempotencyKey(components);
}

export default {
    getConversation: function (conversationId) {
        return apiCore.v2
            .get("/conversations/" + conversationId)
            .then(function (response) {
                return new Conversation(response);
            })
            .catch(function (err) {
                if (
                    err.details &&
                    err.details.response &&
                    err.details.response.status &&
                    err.details.response.status === 404
                ) {
                    //for aged interactions if we get 404, then we will return empty conversationObservable to mock the behaviour
                    return new Conversation({});
                }
                throw err;
            });
    },

    /**
     * getRecordings
     *
     * will retrieve all recordings associated with conversationId that has been passed in
     *
     * @param conversationId
     * @returns {*}
     */
    getRecordings: function (conversationId, formatId, mediaFormats) {
        const queryParameters = [];
        if (formatId) {
            queryParameters.push("formatId=" + formatId);
        }
        if (mediaFormats && mediaFormats.length) {
            queryParameters.push("mediaFormats=" + mediaFormats.join(","));
        }
        //TODO: Handle multiple recordings per unique conversation Id
        return apiCore.v2
            .get(
                "/conversations/" +
                    conversationId +
                    "/recordings" +
                    (queryParameters.length
                        ? "?" + queryParameters.join("&")
                        : "")
            )
            .then(function (response) {
                //returns one or more recording objects
                return response;
            });
    },

    getDownloadRecording: function (
        conversationId,
        recordingId,
        downloadFormatId,
        formatId,
        fileName
    ) {
        const parameters = {
            download: "true",
            fileName: _.isEmpty(fileName) ? recordingId : fileName,
            locale: localeService.getAppLocale(),
        };

        if (downloadFormatId === "emailFormatId") {
            parameters[downloadFormatId] = _.isEmpty(formatId)
                ? "eml"
                : formatId;
        } else if (
            downloadFormatId === "chatFormatId" ||
            downloadFormatId === "messageFormatId"
        ) {
            parameters[downloadFormatId] = _.isEmpty(formatId)
                ? "zip"
                : formatId;
        } else {
            parameters[downloadFormatId] = _.isEmpty(formatId)
                ? "wav"
                : formatId;
        }

        return apiCore.v2.get(
            "/conversations/" + conversationId + "/recordings/" + recordingId,
            parameters
        );
    },

    updateRecording: function (recording, options) {
        return apiCore.v2
            .put(
                "/conversations/" +
                    recording.conversationId +
                    "/recordings/" +
                    (recording.id ? recording.id : recording.recordingId),
                recording,
                options
            )
            .then(function (response) {
                return new Recording(response);
            });
    },

    createAnnotation: function (recording, annotationText, position) {
        return apiCore.v2
            .post(
                "/conversations/" +
                    recording.conversationId +
                    "/recordings/" +
                    recording.id +
                    "/annotations",
                {
                    description: annotationText,
                    location: position,
                    type: "Bookmark",
                }
            )
            .then(function (response) {
                return new Annotation(response);
            });
    },

    getConversationAttributeNames: function () {
        // TODO: Mock data used by qm policy UI!
        const attributes = ko.observableArray([
            "shift",
            "reviewer",
            "favorite",
            "flagged",
        ]);
        return Promise.resolve(attributes);
    },

    getRecordingMediaTypes: function () {
        const mediaTypes = [
            {
                id: "CALL",
                display: ko.i18n.renderString(
                    "qualityManagementService.mediaTypes.call"
                ),
            },
            {
                id: "CHAT",
                display: ko.i18n.renderString(
                    "qualityManagementService.mediaTypes.chat"
                ),
            },
        ];

        return Promise.resolve(ko.observableArray(mediaTypes));
    },

    getConversationDirections: function () {
        const directions = [
            {
                id: "INBOUND",
                display: ko.i18n.renderString(
                    "qualityManagementService.conversationDirections.inbound"
                ),
            },
            {
                id: "OUTBOUND",
                display: ko.i18n.renderString(
                    "qualityManagementService.conversationDirections.outbound"
                ),
            },
        ];

        return Promise.resolve(ko.observableArray(directions));
    },
    getAssignedEvaluations: function (options) {
        const params = _.assign(
            {
                expandAnswerTotalScores: true,
            },
            options
        );
        return apiCore.v2
            .get("/quality/evaluations/query", params)
            .then(function (response) {
                return Promise.resolve(response);
            })
            .then(function (expandedResponse) {
                const pendingEvaluationData = new PendingEvaluations();
                ObservableModel.merge(pendingEvaluationData, expandedResponse);
                return pendingEvaluationData;
            });
    },
    getPendingEvaluations: function (currentUserId) {
        return apiCore.v2
            .get("/quality/evaluations/query", {
                evaluatorUserId: currentUserId,
            })
            .then(function (response) {
                return Promise.resolve(response);
            })
            .then(function (expandedResponse) {
                const pendingEvaluationData = new PendingEvaluations();
                ObservableModel.merge(pendingEvaluationData, expandedResponse);
                return pendingEvaluationData;
            });
    },

    getConversationEvaluations: function (conversationId, options) {
        options = options || {};
        const isAssigneeToggleEnabled = featureToggleService.isEnabled(
            FeatureToggle.EVALUATION_ASSIGNEE
        );

        const isAddScoreToQualitySummaryToggleEnabled =
            featureToggleService.isEnabled(
                FeatureToggle.WEM_1378_ADD_SCORE_TO_QUALITY_SUMMARY
            );

        const expandParams = ["evaluator"];
        if (isAssigneeToggleEnabled) {
            expandParams.push("assignee");
        }
        if (isAddScoreToQualitySummaryToggleEnabled) {
            expandParams.push("agent");
        }

        const params = _.assign(
            {
                expand: expandParams,
                conversationId: conversationId,
                expandAnswerTotalScores: true,
            },
            options
        );
        return apiCore.v2
            .get("/quality/evaluations/query", params)
            .then(function (response) {
                return response;
            });
    },

    getConversationSurveys: function (conversationId) {
        return apiCore.v2
            .get("/quality/conversations/" + conversationId + "/surveys")
            .then(function (response) {
                return response;
            });
    },

    getSurvey: function (surveyId) {
        return apiCore.v2.get(`/quality/surveys/${surveyId}`);
    },

    getAgentEvaluations: function (currentUserId, options) {
        const uri = "/quality/evaluations/query";

        options.agentUserId = currentUserId;
        return apiCore.v2.get(uri, options).then(function (response) {
            const agentEvaluationData = new PendingEvaluations();
            ObservableModel.merge(agentEvaluationData, response);
            return agentEvaluationData;
        });
    },
    getAssignedEvaluation: function (
        conversationId,
        assignedEvaluationId,
        options
    ) {
        return apiCore.v2.get(
            "/quality/conversations/" +
                conversationId +
                "/evaluations/" +
                assignedEvaluationId,
            options
        );
    },

    deleteEvaluation: function (conversationId, assignedEvaluationId) {
        return apiCore.v2.del(
            "/quality/conversations/" +
                conversationId +
                "/evaluations/" +
                assignedEvaluationId
        );
    },

    createCalibration: function (calibration) {
        if (!calibration.calibrator) {
            calibration.calibrator = { id: authService.principalId() };
        }
        return apiCore.v2.post("/quality/calibrations", calibration);
    },
    getCalibrations: function (options) {
        if (!options) {
            options = {};
        }
        if (!options.calibratorId) {
            options.calibratorId = authService.principalId();
        }
        return apiCore.v2.get("/quality/calibrations", options);
    },
    getCalibration: function (calibrationId, options) {
        if (!options) {
            options = {};
        }
        if (!options.calibratorId) {
            options.calibratorId = authService.principalId();
        }
        return apiCore.v2.get(
            "/quality/calibrations/" + calibrationId,
            options
        );
    },
    putCalibration: function (calibration) {
        if (!calibration.calibratorId) {
            calibration.calibrator = { id: authService.principalId() };
        }
        return apiCore.v2.put(
            "/quality/calibrations/" + calibration.id,
            calibration
        );
    },
    deleteCalibration: function (calibrationId, options) {
        if (!options) {
            options = {};
        }
        if (!options.calibratorId) {
            options.calibratorId = authService.principalId();
        }
        return apiCore.v2.del(
            "/quality/calibrations/" + calibrationId,
            options
        );
    },
    updateAssignedEvaluation: function (
        conversationId,
        assignedEvaluationId,
        assignedEvaluation
    ) {
        return apiCore.v2
            .put(
                "/quality/conversations/" +
                    conversationId +
                    "/evaluations/" +
                    assignedEvaluationId,
                assignedEvaluation
            )
            .then(function (response) {
                return response;
            });
    },
    /**
     * Creates an evaluation. When QM_19313_EVALUATION_DEDUPLICATION is on, sends
     * Idempotency-Key for deduplication (see "Create-evaluation deduplication" block above).
     *
     * @param {Object} evaluation - Evaluation payload (conversation, evaluationForm, agent, queue, evaluator, etc.)
     * @returns {Promise} Resolves with created evaluation; backend may return 409 for duplicate key.
     */
    createEvaluation: function (evaluation) {
        let headers;
        if (
            featureToggleService.isEnabled(
                FeatureToggle.QM_19313_EVALUATION_DEDUPLICATION
            )
        ) {
            headers = {
                "Idempotency-Key":
                    generateIdempotencyKeyForCreateEvaluation(evaluation),
            };
        }

        const request = apiCore.v2.post(
            "/quality/conversations/" +
                evaluation.conversation.id +
                "/evaluations",
            evaluation,
            undefined, // parameters
            headers // custom headers
        );

        if (headers) {
            return request.then(function (response) {
                createEvaluationIdempotencyRotation += 1;
                return response;
            });
        }
        return request;
    },
    getRecentEvaluatorActivity: function (options) {
        return apiCore.v2.get("/quality/evaluators/activity", options);
    },
    getAgentActivity: function (options) {
        return apiCore.v2.get("/quality/agents/activity", options);
    },
    createAuditQuery: function (interval, filters, sort) {
        const data = {
            interval,
            filters,
            sort,
        };
        return apiCore.v2.post("/quality/conversations/audits/query", data);
    },
    getAuditQueryStatus: function (transactionId) {
        return apiCore.v2.get(
            "/quality/conversations/audits/query/" + transactionId
        );
    },
    getAuditQueryResults: function (transactionId, cursor, pageSize) {
        const options = {};
        options.pageSize = pageSize;
        options.cursor = cursor;
        options.expand = "user";

        return apiCore.v2.get(
            "/quality/conversations/audits/query/" + transactionId + "/results",
            options
        );
    },
};
